import * as React from 'react';
import { SearchIcon, Building2Icon, CheckIcon, ChevronDownIcon, HomeIcon } from 'lucide-react';
import { useGetSwitchableCouncils, getGetSwitchableCouncilsQueryKey } from '@cdf/api-client-react';
import { useStaffSession } from '@/components/auth/staff-session-provider';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

/**
 * Council switcher — visible only for super_admin.
 *
 * Shows a searchable dropdown of all 79 Victorian councils with open-report
 * counts. Selecting a council sets the acting tenant so all workspace views
 * (queue, assets, map, digital twin) switch to that council's data.
 */
export function CouncilSwitcher() {
  const { staffRole, actingTenantId, homeTenantId, setActingTenant } = useStaffSession();
  const [search, setSearch] = React.useState('');
  const [open, setOpen] = React.useState(false);

  // Only super_admin sees the switcher.
  if (staffRole !== 'super_admin') return null;

  return (
    <CouncilSwitcherInner
      search={search}
      setSearch={setSearch}
      open={open}
      setOpen={setOpen}
      actingTenantId={actingTenantId}
      homeTenantId={homeTenantId}
      setActingTenant={setActingTenant}
    />
  );
}

interface CouncilSwitcherInnerProps {
  search: string;
  setSearch: (v: string) => void;
  open: boolean;
  setOpen: (v: boolean) => void;
  actingTenantId: string | null;
  homeTenantId: string | null;
  setActingTenant: (id: string | null) => void;
}

function CouncilSwitcherInner({
  search,
  setSearch,
  open,
  setOpen,
  actingTenantId,
  homeTenantId,
  setActingTenant,
}: CouncilSwitcherInnerProps) {
  const { data, isLoading } = useGetSwitchableCouncils({
    query: { queryKey: getGetSwitchableCouncilsQueryKey(), enabled: open, staleTime: 30_000 },
  });

  const councils = data?.councils ?? [];

  // Filter by search term (case-insensitive on name or slug).
  const filtered = React.useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return councils;
    return councils.filter(
      (c) =>
        c.name.toLowerCase().includes(term) ||
        c.slug.toLowerCase().includes(term)
    );
  }, [councils, search]);

  // Find the currently selected council for display.
  const selected = councils.find((c) => c.id === actingTenantId);
  const isActing = !!actingTenantId && actingTenantId !== homeTenantId;

  const handleSelect = (id: string) => {
    setActingTenant(id);
    setOpen(false);
    setSearch('');
  };

  const handleResetHome = () => {
    setActingTenant(null);
    setOpen(false);
    setSearch('');
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(
            'gap-2 font-medium',
            isActing && 'border-primary/50 bg-primary/5 text-primary'
          )}
        >
          <Building2Icon className="size-4" />
          <span className="max-w-[160px] truncate">
            {selected?.name ?? 'Home Council'}
          </span>
          {isActing && (
            <Badge variant="secondary" className="ml-1 px-1.5 py-0 text-[10px]">
              Acting
            </Badge>
          )}
          <ChevronDownIcon className="size-3.5 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-72 p-0">
        {/* Search input */}
        <div className="border-b p-2">
          <div className="relative">
            <SearchIcon className="absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search 79 councils…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-9 pl-9"
              autoFocus
            />
          </div>
        </div>

        {/* Scrollable council list */}
        <div className="max-h-72 overflow-y-auto">
          {isLoading ? (
            <div className="px-3 py-4 text-center text-sm text-muted-foreground">
              Loading councils…
            </div>
          ) : filtered.length === 0 ? (
            <div className="px-3 py-4 text-center text-sm text-muted-foreground">
              No councils match “{search}”.
            </div>
          ) : (
            filtered.map((council) => {
              const isActive = council.id === actingTenantId;
              return (
                <DropdownMenuItem
                  key={council.id}
                  className="flex cursor-pointer items-center gap-2 py-2"
                  onClick={() => handleSelect(council.id)}
                >
                  <span
                    className="size-2.5 shrink-0 rounded-full"
                    style={{ backgroundColor: council.brandingColor || '#6366f1' }}
                    aria-hidden
                  />
                  <span className="min-w-0 flex-1 truncate text-sm">{council.name}</span>
                  {council.openReportCount > 0 && (
                    <Badge variant="secondary" className="shrink-0 text-[10px]">
                      {council.openReportCount}
                    </Badge>
                  )}
                  {isActive && <CheckIcon className="size-4 shrink-0 text-primary" />}
                </DropdownMenuItem>
              );
            })
          )}
        </div>

        {/* Footer: reset to home */}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="flex cursor-pointer items-center gap-2 py-2"
          onClick={handleResetHome}
        >
          <HomeIcon className="size-4" />
          <span className="text-sm">Return to Home Council</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
